import React from "react";
import { useLang } from "@/i18n";
import { LABEL_W, STAT_W, HEAD_H, addDays } from "@/components/dashboard/planning/planningConstants";

// Datumbalk bovenaan het raster
export const PlanningHeaderBar = ({ data, cell, scrollX, totalW, months, firstCol, lastCol }) => {
  const { t } = useLang();
  return (
    <div className="absolute top-0 left-0 right-0 z-20 overflow-hidden border-b border-sky-200/80"
      data-testid="planning-header" style={{ height: HEAD_H, background: "linear-gradient(180deg,rgba(255,255,255,.96),rgba(226,238,250,.9))", backdropFilter: "blur(14px)" }}>
      <div className="absolute inset-y-0 left-0 z-10 border-r border-sky-200/80 flex items-end pb-1 pl-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground" style={{ width: LABEL_W }}>{t("pitch")}</div>
      <div className="absolute inset-y-0" style={{ left: LABEL_W, right: STAT_W, overflow: "hidden" }}>
        <div style={{ transform: `translateX(${-scrollX}px)`, width: totalW, position: "relative", height: "100%" }}>
          {months.map((m) => (
            <span key={m.i} className="absolute top-1.5 text-[11px] font-bold text-foreground/90 whitespace-nowrap px-1"
              style={{ left: m.i * cell }}>{m.label}</span>
          ))}
          {Array.from({ length: Math.max(0, lastCol - firstCol) }, (_, k) => {
            const i = firstCol + k;
            const iso = addDays(data.start, i);
            const wd = new Date(`${iso}T00:00:00Z`).getUTCDay();
            return (
              <span key={i} className={`absolute bottom-0.5 text-center text-[9px] tabular-nums ${wd === 0 || wd === 6 ? "text-rose-500 font-semibold" : "text-muted-foreground"}`}
                style={{ left: i * cell, width: cell }}>{iso.slice(8, 10)}</span>
            );
          })}
          <div className="absolute top-0 bottom-0 w-[2px] bg-rose-500" style={{ left: data.today_index * cell }} />
        </div>
      </div>
      <div className="absolute inset-y-0 right-0 z-10 border-l border-sky-200/80 flex items-end justify-end pb-1 pr-2 text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground text-right" style={{ width: STAT_W }}>{data.year}</div>
    </div>
  );
};

// Plaatslabels links en de jaarbezetting rechts
export const PlanningSideColumns = ({ data, rowH, scrollY, totalH, firstRow, lastRow }) => {
  const visible = Array.from({ length: Math.max(0, lastRow - firstRow) }, (_, k) => firstRow + k);
  return (
    <>
      <div className="absolute left-0 z-10 overflow-hidden border-r border-sky-200/80" style={{ top: HEAD_H, bottom: 28, width: LABEL_W, background: "linear-gradient(90deg,#ffffff,#e8f2fc)" }}>
        <div style={{ transform: `translateY(${-scrollY}px)`, position: "relative", height: totalH }}>
          {visible.map((i) => (
            <div key={data.rows[i].id} className="absolute left-0 right-0 flex items-center pl-2 text-[11px] font-bold text-slate-800 border-b border-sky-100"
              style={{ top: i * rowH, height: rowH }} data-testid={`planning-label-${data.rows[i].label}`}>{data.rows[i].label}</div>
          ))}
        </div>
      </div>

      <div className="absolute right-0 z-10 overflow-hidden border-l border-sky-200/80" style={{ top: HEAD_H, bottom: 28, width: STAT_W, background: "linear-gradient(270deg,#ffffff,#e8f2fc)" }}>
        <div style={{ transform: `translateY(${-scrollY}px)`, position: "relative", height: totalH }}>
          {visible.map((i) => (
            <div key={data.rows[i].id} className="absolute right-0 left-0 flex items-center justify-end gap-1.5 pr-2 border-b border-sky-100 text-[10px] tabular-nums text-slate-500"
              style={{ top: i * rowH, height: rowH }} data-testid={`planning-stat-${data.rows[i].label}`}>
              <span>{data.rows[i].booked}/{data.days_in_year}</span>
              <span className="font-bold text-slate-900">{data.rows[i].pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

// Bezetting per maand boven het raster
export const PlanningMonthBars = ({ data, cell, scrollX, totalW, maxPct }) => (
  <div className="mx-5 mb-2 relative h-10 rounded-xl overflow-hidden border border-sky-200/70"
    style={{ background: "linear-gradient(180deg,#f8fbff,#e9f1fa)" }} data-testid="planning-occupancy">
    <span className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground" style={{ width: LABEL_W }}>%</span>
    <div className="absolute inset-y-0 overflow-hidden" style={{ left: LABEL_W, right: STAT_W }}>
      <div style={{ transform: `translateX(${-scrollX}px)`, width: totalW, height: "100%", position: "relative" }}>
        {(data.months || []).map((m) => (
          <div key={m.month} className="absolute bottom-0 top-0" style={{ left: m.col * cell + 1, width: Math.max(10, m.days * cell - 2) }}
            title={`${m.month} · ${m.pct}%`} data-testid={`occ-${m.month}`}>
            <div className="absolute bottom-0 left-0 right-0 rounded-t-[3px] transition-[height]"
              style={{ height: `${Math.max(3, Math.min(100, m.pct * 100 / maxPct))}%`, background: m.pct >= 75 ? "linear-gradient(180deg,#fb7185,#e11d48)" : m.pct >= 40 ? "linear-gradient(180deg,#fcd34d,#f59e0b)" : "linear-gradient(180deg,#7dd3fc,#0ea5e9)" }} />
            <span className="absolute inset-x-0 bottom-0.5 text-center text-[9px] font-bold tabular-nums text-foreground/90">{m.pct >= 1 ? `${Math.round(m.pct)}%` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);
